import { useCallback, useState } from "react";
import { useMount } from "react-use";
import { t } from "ttag";

import { useLazyGenerateRandomTokenQuery } from "metabase/api/util";
import { useMetadataToasts } from "metabase/common/hooks";
import {
  ActionIcon,
  Alert,
  Button,
  Flex,
  Group,
  Icon,
  Loader,
  Modal,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "metabase/ui";

type SetupKeyDialogProps = {
  currentValue?: string;
  onClose: () => void;
  onConfirm: (secretKey: string) => void;
};

const MIN_SECRET_LENGTH = 8;

export const SetupKeyModal = (props: SetupKeyDialogProps) => {
  const { currentValue, onClose, onConfirm } = props;
  const [secretValue, setSecretKey] = useState<string>("");
  const { sendErrorToast, sendSuccessToast } = useMetadataToasts();
  const [generateRandomToken, { isFetching: isGenerating }] =
    useLazyGenerateRandomTokenQuery();

  const generateToken = useCallback(async () => {
    try {
      const result = await generateRandomToken().unwrap();
      setSecretKey(result.token);
    } catch {
      sendErrorToast(t`Error generating secret key.`);
    }
  }, [generateRandomToken, sendErrorToast]);

  useMount(() => {
    void generateToken();
  });

  return (
    <Modal onClose={onClose} opened title={t`Set up secret key`}>
      <Stack>
        <Flex align="end" gap="1rem">
          <TextInput
            onChange={(event) => setSecretKey(event.target.value || "")}
            value={secretValue}
            disabled={isGenerating}
            rightSection={
              isGenerating ? (
                <Loader size="xs" />
              ) : (
                <Tooltip label={t`Copy to clipboard`}>
                  <ActionIcon
                    aria-label={t`Copy to clipboard`}
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(secretValue);
                        sendSuccessToast(t`Secret key copied to clipboard`);
                      } catch {
                        sendErrorToast(
                          t`Error copying secret key to clipboard.`,
                        );
                      }
                    }}
                  >
                    <Icon name="copy" />
                  </ActionIcon>
                </Tooltip>
              )
            }
            flex="1 0 auto"
            aria-label={t`New secret key`}
          />
          <Button flex="0 0 auto" variant="filled" onClick={generateToken}>
            {t`Regenerate key`}
          </Button>
        </Flex>
        <Stack gap="sm">
          <Alert size="compact" color="warning">
            {t`Make sure you copy this key now and save it in a safe place.`}
            <Text fw="bold" component="strong" display="block">
              {t`You won't be able to see it again.`}
            </Text>
          </Alert>
          {!!currentValue && (
            <Alert size="compact" color="warning" mt="lg">
              <Text component="strong" fw="bold">
                {t`This will cause existing tokens to stop working until the identity provider is updated with the new key.`}
              </Text>
            </Alert>
          )}
        </Stack>

        <Group justify="flex-end" gap="sm" mt="sm">
          <Button onClick={onClose} variant="subtle">{t`Cancel`}</Button>
          <Button
            disabled={secretValue.length < MIN_SECRET_LENGTH}
            onClick={() => onConfirm(secretValue)}
            variant="filled"
          >
            {t`Done`}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
